import { FeedFormat, IPuzzleData } from "./types";
import { fetchAllDatesRaw } from "./getYearsInfo";
import { dateToYYYYMMDD, getCalendarEntries } from "./calendarData";
import { getFeedSlug } from "./sdk/getFeedSlug";
import convertArrowWord from "./converters/convertArrowWord";
import convertCN2I from "./converters/convertCN2I";
import convertHearst from "./converters/convertHearst";
import convertLarousse from "./converters/convertLarousse";
import convertREACH from "./converters/convertREACH";
import convertTCA from "./converters/convertTCA";
import convertTCAMini from "./converters/convertTCAMini";

type Converter = (rawData: any) => IPuzzleData;

const converters: Record<FeedFormat, Converter> = {
  [FeedFormat.GERMAN_ARROW_WORD]: convertArrowWord,
  [FeedFormat.CN2I_HANNEQUART]: convertCN2I,
  [FeedFormat.HEARST]: convertHearst,
  [FeedFormat.LAROUSSE]: convertLarousse,
  [FeedFormat.REACH]: convertREACH,
  [FeedFormat.REACH_BAUER]: convertTCA,
  [FeedFormat.TCA]: convertTCA,
  [FeedFormat.TCA_MINI]: convertTCAMini,
};

export async function fetchRawPuzzleData(url: string): Promise<any> {
  return new Promise((resolve) => {
    const req = new XMLHttpRequest();
    req.open('GET', url, true);
    req.addEventListener('load', () => resolve(JSON.parse(req.responseText)));
    req.send();
  });
}

export async function getPuzzleFromURL(feedSlug: string, url: string): Promise<IPuzzleData> {
  const rawData = await fetchRawPuzzleData(url);
  const converter: Converter = converters[rawData.metaData.format];
  if (!converter) throw new Error(`No converter for ${rawData.metaData.format}`);
  return converter(rawData);
}

export async function getPuzzleFromDate(date: string): Promise<IPuzzleData | null> {
  const allDatesRaw = await fetchAllDatesRaw();
  const dateToLookFor = findClosestDateInPast(date, allDatesRaw);

  if (dateToLookFor === null) {
    throw new Error('Could not find proper date');
  }

  const year = parseInt(dateToLookFor.split('-')[0], 10);
  const month = parseInt(dateToLookFor.split('-')[1], 10);
  const dayEntries = await getCalendarEntries(year, month - 1, '0');
  const day = dayEntries.find((entry) => entry.date === dateToLookFor);

  if (day && day.puzzleUrl) {
    return getPuzzleFromURL(getFeedSlug(), day.puzzleUrl);
  } else {
    throw new Error('Could not find proper day');
  }
}

function findClosestDateInPast(date: string, dateArray: string[]): string | null {
  if (dateArray.includes(date)) return date;

  const currentDate = new Date(date);

  const closestDate = dateArray
    .reverse()
    .map((dateStr) => new Date(dateStr))
    .find((dateObj) => dateObj.getTime() < currentDate.getTime());
  return closestDate ? dateToYYYYMMDD(closestDate) : null;
}
